<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading">
        <table-paginated-subscriptions
          :is-component-loading="isRequestingNow"
          :paginated-object="paginatedObject"
          @edit-item="onPaginatedItemEdit"
          @delete-item="onPaginatedItemDelete"
        ></table-paginated-subscriptions>
        <pagination-listing
          :pagination-data="paginationData"
          @go-prev="changePage('-')"
          @go-next="changePage('+')"
        ></pagination-listing>
      </v-flex>
      <modal-loading :is-show="isTableLoading"></modal-loading>
      <!-- Edit Modal -->
      <edit-modal-subscription
        :contact="contact"
        :item="currentEditableItem"
        :is-show-dialog="isShowSubscriptionDialog"
        :is-modal-loading="isLoadingSubscriptionDialog"
        :action-label="userDialogActionLabel"
        :errors="errorsSubscriptionDialog"
        @on-close="onCloseSubscriptionDialog"
        @on-action="onActionSubscriptionDialog"
      />
      <v-btn
        color="pink"
        dark
        fixed
        bottom
        right
        fab
        @click="currentEditableItem = {}; isShowSubscriptionDialog = true"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <!-- End: Edit Modal -->
      <accept-dialog
        :is-open-dialog="isShowDeleteDialog"
        @on-close="isShowDeleteDialog=false"
        @on-accept="onAcceptDeletion"
      >
        <div class="pt-6">
          {{acceptModalText}}
        </div>
      </accept-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
/* constants */
import { PER_PAGE } from '../../constants/listing';
import { DATE_VIEW, DATETIME_ISO } from '../../constants/dates';
/* utils */
import { deepMerge, prepareValidationMessages, strReplaceAll } from '../../utils/index';
/* proxies */
import SubscriptionProxy from '../../proxies/Order/SubscriptionProxy';
import ContactProxy from '../../proxies/Contact/ContactProxy';
/* transformers */
import SubscriptionTransformer from '../../transformers/Orders/SubscriptionTransformer';
/* mixins */
import defaultListingMixin from '../../mixins/listing/defaultListingMixin';
import updatePageContentMixin from '../../mixins/updatePageContentMixin';
import paginationMixin from '../../mixins/listing/paginationMixin';
/* components */
import ModalLoading from '../../components/loaders/ModalLoading.vue';
import TablePaginatedSubscriptions from '../../components/listings/TablePaginatedSubscriptions.vue';
import EditModalSubscription from '../../components/listings/EditModalSubscription.vue';
import PaginationListing from '../../components/listings/PaginationListing.vue';
import AcceptDialog from '../../components/AcceptDialog.vue';

export default {
  name: 'ContactsSubscriptions',
  components: {
    ModalLoading,
    AcceptDialog,
    TablePaginatedSubscriptions,
    PaginationListing,
    EditModalSubscription,
  },
  mixins: [
    updatePageContentMixin,
    paginationMixin,
    defaultListingMixin,
  ],
  data() {
    return {
      contact: {},
      /* Page management */
      pageLoading: false,
      /* Create - Edit user */
      isLoadingSubscriptionDialog: false,
      errorsSubscriptionDialog: {},
      isShowSubscriptionDialog: false,
      currentEditableItem: {},
      /* Delete */
      isShowDeleteDialog: false,
      /* Listing management */
      isTableLoading: false,
      searchParams: {
        contact_uuid: null,
        page: {
          number: 1,
          size: PER_PAGE,
        },
      },
    };
  },
  created() {
    this.searchParams.contact_uuid = this.$route.params.userUuid;
    this.isTableLoading = true;
    this.fetchData();
  },
  computed: {
    /* edit modal */
    userDialogActionLabel() {
      if (this.isShowSubscriptionDialog) {
        return (this.currentEditableItem.uuid)
          ? this.lang(this.$store, 'sap/listings.global.edit.save')
          : this.lang(this.$store, 'sap/listings.global.edit.create');
      }
      return '';
    },
    /* accept modal */
    acceptModalText() {
      return strReplaceAll(this.lang(this.$store, 'sap/listings.global.delete.question'), {
        user: (this.currentEditableItem && this.currentEditableItem.start) ? `${this.currentEditableItem.start.format(DATE_VIEW)} - ${this.currentEditableItem.end.format(DATE_VIEW)}` : '',
      });
    },
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
  },
  methods: {
    fetchData() {
      this.isRequestingNow = true;

      new ContactProxy().find(this.$route.params.userUuid)
        .then(response => {
          this.contact = response.data.data.contact;
        });

      new SubscriptionProxy()
        .setParameters(this.searchParams)
        .all()
        .then(response => {
          // eslint-disable-next-line no-param-reassign
          response.data.data.subscriptions.data = SubscriptionTransformer
            .fetchCollection(response.data.data.subscriptions.data);
          this.paginatedObject = response.data.data.subscriptions;
          // console.warn(this.paginatedObject);
        })
        .catch(error => {
          console.warn(error);
          this.paginatedObject = {};
        })
        .finally(() => {
          this.isTableLoading = false;
          this.isRequestingNow = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    onPaginatedItemEdit(item) {
      this.currentEditableItem = item;
      this.isShowSubscriptionDialog = true;
    },
    onPaginatedItemDelete(item) {
      this.currentEditableItem = item;
      this.isShowDeleteDialog = true;
    },
    onFilter(event) {
      const newSearchParams = Object.assign({}, event);
      if (_.isString(newSearchParams.role)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.role = [newSearchParams.role];
      }
      this.searchParams = deepMerge(this.searchParams, deepMerge(newSearchParams, {
        page: {
          number: 1,
        },
      }));
    },
    onCloseSubscriptionDialog() {
      this.isShowSubscriptionDialog = false;
    },
    onActionSubscriptionDialog(item) {
      if (_.isObject(item)) {
        this.isLoadingSubscriptionDialog = true;
        let client = new SubscriptionProxy();
        if (this.currentEditableItem.uuid) {
          const subscription = {
            start: item.start.format(DATETIME_ISO),
            end: item.end.format(DATETIME_ISO),
          };
          client = client.update(this.currentEditableItem.uuid, subscription);
        } else {
          const subscription = {
            start: item.start.format(DATETIME_ISO),
            end: item.end.format(DATETIME_ISO),
            address_uuid: (_.isString(item.address)) ? item.address : item.address.uuid,
          };
          client = client.create(subscription);
        }

        client.then(response => {
          this.currentEditableItem = SubscriptionTransformer.fetch(response.data.data.subscription);
          this.$store.dispatch('page/updateCurrentPageContent');
          this.isShowSubscriptionDialog = false;
        })
          .catch(response => {
            this.errorsSubscriptionDialog = prepareValidationMessages(response.data.data);
          })
          .finally(() => {
            this.isLoadingSubscriptionDialog = false;
          });
      }
    },
    onAcceptDeletion() {
      this.isRequestingNow = true;
      new SubscriptionProxy().destroy(this.currentEditableItem.uuid)
        .catch(response => {
          console.error(response);
          alert(`Error: ${response.status}`);
          this.isRequestingNow = false;
        })
        .then(() => {
          this.fetchData();
        });
    },
  },
};
</script>
